/**
 * Choosing which task an hour is logged against.
 *
 * ## Why three optional selects and not a cascade
 *
 * The Task select used to be one flat `Project · Task` list. How bad that was
 * depended entirely on who was looking: a contractor has "Only tasks assigned
 * to me" checked by default and holds a handful, which is fine — while an
 * owner logging their own time got every task they hold, with no client or
 * application shown anywhere. That is F7 again on a different screen.
 * `docs/FINDINGS.md` F24.
 *
 * So the three levels above are offered, and every one of them is **optional**.
 * `PlacementSelect` on the board walks the same nouns and requires all of them,
 * because there you are *setting* where a task lives and a half-answer is not a
 * placement. Here you are *finding* work that already exists, and the common
 * case — a contractor with three tasks — should still be one choice, not four.
 * The model for this screen is the board's filter bar, not its placement
 * control.
 *
 * ## Narrowing is a query, not a filter over what we already have
 *
 * Every level is passed to `useTasks`, exactly as `BoardFilters` passes them.
 * Filtering a list already fetched would mean fetching every task to show four
 * of them, and would quietly disagree with the board about what a filter means.
 */
import { useEffect, useState } from 'react';

import { Select } from '@/components';
import type { SelectOption } from '@/components';
import { useApplications, useClients, useProjects, useTasks } from '@/lib/queries';
import { narrowScope, type TaskScope } from '@/lib/scope';
import { taskPickerHint } from '@/lib/task-picker';

export interface TaskPickerProps {
  /** Whose tasks "only mine" means. Hours are always logged against the caller. */
  userId: string;
  value: string;
  onChange: (taskId: string) => void;
  error?: string | null;
  /**
   * The entry being edited, if any.
   *
   * Two jobs: it seeds the filters so an edit opens showing where the task
   * lives, and it keeps the task selectable even when the filters exclude it.
   */
  editing?: { taskId: string; taskTitle: string; projectName: string } | null;
}

const optionsFrom = <T extends { id: string; name: string }>(rows: T[] | undefined): SelectOption[] =>
  (rows ?? []).map((row) => ({ value: row.id, label: row.name }));

export function TaskPicker({ userId, value, onChange, error = null, editing = null }: TaskPickerProps) {
  const [onlyMine, setOnlyMine] = useState(true);
  const [scope, setScope] = useState<TaskScope>({});

  const clients = useClients();
  const applications = useApplications(scope.clientId ? { clientId: scope.clientId } : {});
  const projects = useProjects({
    ...(scope.clientId ? { clientId: scope.clientId } : {}),
    ...(scope.applicationId ? { applicationId: scope.applicationId } : {}),
  });

  const tasks = useTasks({ ...scope, ...(onlyMine ? { assigneeId: userId } : {}) });

  /*
   * Open an edit showing where that task actually lives.
   *
   * A `TaskDto` carries `clientId` and `projectId` outright, so this is a
   * lookup rather than a walk back up through the feature - which is also why
   * it cannot reproduce F22: there is no chain to re-derive and nothing for a
   * later render to race.
   *
   * Only while the task is genuinely in the fetched set. Seeding from a task
   * the current filters exclude would move the filters somewhere the person
   * did not ask to go.
   */
  useEffect(() => {
    if (!editing) return;
    const task = (tasks.data ?? []).find((row) => row.id === editing.taskId);
    if (!task) return;
    setScope((current) =>
      current.clientId || current.projectId
        ? current
        : { clientId: task.clientId, projectId: task.projectId },
    );
    // Deliberately keyed on the entry and the fetched tasks only. `scope` is
    // what this writes, and the guard above already makes it run at most once
    // per entry - including it would loop.
  }, [editing, tasks.data]);

  const pick = (level: Parameters<typeof narrowScope>[1]) => (id: string) => {
    setScope((current) => narrowScope(current, level, id));
    // The chosen task almost certainly falls outside the new filters, and a
    // hidden selection that still submits is worse than an obvious empty one.
    onChange('');
  };

  /*
   * Still labelled `Project · Task`, even though a project filter now exists.
   * The filters are optional, so the common case is no filter at all - and a
   * bare title in an unfiltered list is the context-free label F7 was about.
   * The projects query above is scoped identically to the tasks query, so its
   * names cover whatever is listed here.
   */
  const projectNames = new Map((projects.data ?? []).map((project) => [project.id, project.name]));

  const taskOptions: SelectOption[] = (tasks.data ?? [])
    .map((task) => ({
      value: task.id,
      label: `${projectNames.get(task.projectId) ?? 'Project'} · ${task.title}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  /*
   * The entry being edited may hang off a task the filters exclude - it was
   * reassigned, or "only mine" is on and it never was theirs. Without this the
   * select shows blank and the first save clears the task.
   */
  if (editing && !taskOptions.some((option) => option.value === editing.taskId)) {
    taskOptions.unshift({
      value: editing.taskId,
      label: `${editing.projectName} · ${editing.taskTitle}`,
    });
  }

  /*
   * An empty list has four causes and used to look identical for all of them.
   * See `lib/task-picker.ts` - the one that cost time was "no tasks exist",
   * because a feature on its own has nothing to log against.
   */
  const emptiness = taskPickerHint({
    loading: tasks.isPending,
    taskCount: taskOptions.length,
    onlyMine,
    narrowed: Boolean(scope.clientId ?? scope.applicationId ?? scope.projectId),
  });

  return (
    <div className="flex flex-col gap-3 rounded-md border border-line bg-sunken/60 p-3">
      {/* One per row at every width. Three narrow selects side by side put
          the client, the application and the project in a line that reads as
          one control rather than three decisions. */}
      <div className="grid gap-3">
        <Select
          label="Client"
          placeholder="All clients"
          options={optionsFrom(clients.data)}
          value={scope.clientId ?? ''}
          onChange={(event) => pick('clientId')(event.target.value)}
        />
        <Select
          label="Application"
          placeholder="All applications"
          options={optionsFrom(applications.data)}
          value={scope.applicationId ?? ''}
          onChange={(event) => pick('applicationId')(event.target.value)}
        />
        <Select
          label="Project"
          placeholder="All projects"
          options={optionsFrom(projects.data)}
          value={scope.projectId ?? ''}
          onChange={(event) => pick('projectId')(event.target.value)}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Select
          label="Task"
          required
          placeholder={tasks.isPending ? 'Loading tasks…' : 'Choose a task'}
          options={taskOptions}
          value={value}
          error={error}
          hint={emptiness ?? undefined}
          disabled={tasks.isPending}
          onChange={(event) => onChange(event.target.value)}
        />
        <label className="inline-flex w-fit cursor-pointer items-center gap-2 text-[0.8125rem] text-muted">
          <input
            type="checkbox"
            checked={onlyMine}
            onChange={(event) => setOnlyMine(event.target.checked)}
            className="size-3.5 accent-[var(--accent)]"
          />
          Only tasks assigned to me
        </label>
      </div>
    </div>
  );
}
